import React, { useMemo } from 'react';
import styled from 'styled-components';
import moment from 'moment';

import { Display1, Display2, Text } from '../../../../../components';
import { questionShape } from '../../../../../propsShape';
import { DifferenceIndicator } from '../DifferenceIndicator';
// Extract currency/percentage symbols and clean values for calculation
const cleanValue = (value) => {
  if (!value) return null;
  return String(value).replace(/[^0-9.-]/g, '');
};

// Extract currency symbol from the beginning or percentage symbol from the end
const extractSymbol = (value) => {
  if (!value) return '';
  const valueStr = String(value);

  // Check for currency symbol at the beginning
  if (/^[^\d.-]/.test(valueStr)) {
    return valueStr.charAt(0);
  }

  // Check for percentage symbol at the end
  if (valueStr.endsWith('%')) {
    return '%';
  }

  // no symbol for absolute values
  return null;
};

const DifferenceContainer = styled.span`
  color: ${({ isPositive, isNegative }) => {
    if (isPositive) return 'var(--success-default)';
    if (isNegative) return 'var(--critical-default)';
    return 'var(--content-muted)';
  }};
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const SingleValueAnswerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SingleValueAnswer = ({ question }) => {
  const isMetrics = useMemo(
    () => question.answer.isMetricsSingleValueAnswer,
    [question.answer.isMetricsSingleValueAnswer]
  );

  // Use the appropriate getter based on the answer type
  const singleValue = isMetrics
    ? question.answer.getMetricsSingleValueAnswer
    : question.answer.getSingleValueAnswer;

  // Calculate difference if previous version exists
  const previousValue = isMetrics
    ? question.previousVersion?.answer.getMetricsSingleValueAnswer
    : question.previousVersion?.answer.getSingleValueAnswer;

  const difference = previousValue
    ? Number(cleanValue(singleValue)) - Number(cleanValue(previousValue))
    : null;

  const isPositive = difference > 0;
  const isNegative = difference < 0;

  const percentageChange = previousValue
    ? (Math.abs(difference / Number(cleanValue(previousValue))) * 100).toFixed(2)
    : null;

  const symbol = extractSymbol(singleValue);
  const isSymbolPrefix = symbol && symbol !== '%';

  // Format dates using moment
  const formatDate = (date) => moment(date).format('MMM D, YYYY');
  const currentDate = formatDate(question.createdAt);
  const previousDate = question.previousVersion
    ? formatDate(question.previousVersion.createdAt)
    : null;

  return (
    <SingleValueAnswerContainer>
      {question.previousVersion && difference !== null && (
        <DifferenceContainer isPositive={isPositive} isNegative={isNegative}>
          <DifferenceIndicator
            isPositive={isPositive}
            difference={difference}
            percentageChange={percentageChange}
            symbol={symbol}
            isSymbolPrefix={isSymbolPrefix}
          />
        </DifferenceContainer>
      )}
      {previousDate && !moment(currentDate).isSame(previousDate, 'day') && (
        <Text $small $bold $color='var(--content-subtitle)'>
          {currentDate} vs {previousDate}
        </Text>
      )}
      {singleValue.length > 20 ? (
        <Display1>{singleValue}</Display1>
      ) : (
        <Display2>{singleValue}</Display2>
      )}
    </SingleValueAnswerContainer>
  );
};

SingleValueAnswer.propTypes = {
  question: questionShape.isRequired
};

export { SingleValueAnswer };
